'use client';

import { CoreEvent, eventNames } from '@carvana/core-events';
import { setReferrer } from '@carvana/eventable-clickstream';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { markSpaNavigationStart } from '~/utilities/navigationTiming';

function getTopLevelSegment(pathname: string) {
  return pathname.split('/')[1] ?? '';
}

function buildFullPath(
  pathname: string | null,
  searchParams: ReadonlyURLSearchParams | null
) {
  const query = searchParams?.toString();

  return query ? `${pathname ?? ''}?${query}` : pathname ?? '';
}

// Fires a core event when the top-level path segment changes (e.g. /cars → /vehicle/[id]),
// which indicates a page-level SPA navigation rather than an in-page route change.
export default function SpaNavigationListener() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const previousPathname = useRef(pathname);
  const previousFullPath = useRef(buildFullPath(pathname, searchParams));

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const link = (event.target as Element).closest('a');
      if (link?.href.startsWith(window.location.origin) && !link.target) {
        const url = new URL(link.href);
        if (
          getTopLevelSegment(url.pathname) !==
          getTopLevelSegment(window.location.pathname)
        ) {
          markSpaNavigationStart();
        }
      }
    };

    const handlePopstate = () => {
      if (
        getTopLevelSegment(window.location.pathname) !==
        getTopLevelSegment(previousPathname.current ?? '')
      ) {
        markSpaNavigationStart();
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    window.addEventListener('popstate', handlePopstate);

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  // useLayoutEffect (not useEffect) so setReferrer() commits synchronously,
  // before the browser paints — closing the window as tightly as possible
  // ahead of the newly-mounted page's own trackEvent() calls, which defer
  // their actual referrer read to a later microtask via async envelope
  // construction. Does not update previousPathname.current — the effect
  // below does that, once, after both effects have read it.
  useLayoutEffect(() => {
    const previousSegment = getTopLevelSegment(previousPathname.current ?? '');
    const currentSegment = getTopLevelSegment(pathname ?? '');

    if (previousSegment !== currentSegment && previousFullPath.current) {
      setReferrer(window.location.origin + previousFullPath.current);
    }
  }, [pathname, searchParams]);

  useEffect(() => {
    const previousSegment = getTopLevelSegment(previousPathname.current ?? '');
    const currentSegment = getTopLevelSegment(pathname ?? '');

    if (previousSegment !== currentSegment) {
      CoreEvent.emit(eventNames.header.refreshOnSpaNavigation, undefined);
    }

    previousPathname.current = pathname;
    previousFullPath.current = buildFullPath(pathname, searchParams);
  }, [pathname, searchParams]);

  return null;
}
